import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './useApiClient';
import type {
  CreateWorkflowTypePayload,
  CreateWorkflowTemplatePayload,
  UpdateWorkflowTemplatePayload,
  CreateWorkflowVariablePayload,
  CreateWorkflowTypeDefaultPayload,
  ResolutionTestRequest,
  CreateJobFromTemplatePayload,
} from '@/lib/types';

// Workflow Types
export const useWorkflowTypes = (enabled = true) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['workflow-types'],
    queryFn: () => api!.getWorkflowTypes(),
    staleTime: 60_000, // Types rarely change
    enabled: !!api && enabled,
  });
};

export const useCreateWorkflowType = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateWorkflowTypePayload) => api!.createWorkflowType(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-types'] });
    },
  });
};

export const useUpdateWorkflowType = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, payload }: { name: string; payload: { description?: string } }) =>
      api!.updateWorkflowType(name, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workflow-types'] });
      queryClient.invalidateQueries({ queryKey: ['workflow-type', variables.name] });
    },
  });
};

export const useDeleteWorkflowType = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => api!.deleteWorkflowType(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-types'] });
    },
  });
};

// Workflow Variable Types (read-only)
export const useWorkflowVariableTypes = (enabled = true) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['workflow-variable-types'],
    queryFn: () => api!.getWorkflowVariableTypes(),
    staleTime: Infinity, // These never change
    enabled: !!api && enabled,
  });
};

// Workflow Templates
export const useWorkflowTemplates = (
  params?: { type_name?: string; environment?: string; limit?: number },
  enabled = true
) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['workflow-templates', params],
    queryFn: () => api!.getWorkflowTemplates(params),
    staleTime: 30_000,
    enabled: !!api && enabled,
  });
};

export const useWorkflowTemplate = (id: string, enabled = true) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['workflow-template', id],
    queryFn: () => api!.getWorkflowTemplate(id),
    enabled: !!api && !!id && enabled,
  });
};

export const useCreateWorkflowTemplate = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateWorkflowTemplatePayload) => api!.createWorkflowTemplate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-templates'] });
    },
  });
};

export const useUpdateWorkflowTemplate = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateWorkflowTemplatePayload }) =>
      api!.updateWorkflowTemplate(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workflow-templates'] });
      queryClient.invalidateQueries({ queryKey: ['workflow-template', variables.id] });
    },
  });
};

export const useDeleteWorkflowTemplate = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api!.deleteWorkflowTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-templates'] });
    },
  });
};

export const useTemplateValidation = (id: string, enabled = true) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['template-validation', id],
    queryFn: () => api!.getTemplateValidation(id),
    enabled: !!api && !!id && enabled,
  });
};

// Workflow Variables
export const useTemplateVariables = (templateId: string, enabled = true) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['template-variables', templateId],
    queryFn: () => api!.getTemplateVariables(templateId),
    enabled: !!api && !!templateId && enabled,
  });
};

export const useCreateTemplateVariable = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      payload,
    }: {
      templateId: string;
      payload: CreateWorkflowVariablePayload;
    }) => api!.createTemplateVariable(templateId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['template-variables', variables.templateId] });
      queryClient.invalidateQueries({ queryKey: ['workflow-template', variables.templateId] });
      queryClient.invalidateQueries({ queryKey: ['template-validation', variables.templateId] });
    },
  });
};

export const useUpdateTemplateVariable = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      variableId,
      payload,
    }: {
      templateId: string;
      variableId: string;
      payload: Partial<CreateWorkflowVariablePayload>;
    }) => api!.updateTemplateVariable(templateId, variableId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['template-variables', variables.templateId] });
      queryClient.invalidateQueries({ queryKey: ['workflow-template', variables.templateId] });
      queryClient.invalidateQueries({ queryKey: ['template-validation', variables.templateId] });
    },
  });
};

export const useDeleteTemplateVariable = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, variableId }: { templateId: string; variableId: string }) =>
      api!.deleteTemplateVariable(templateId, variableId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['template-variables', variables.templateId] });
      queryClient.invalidateQueries({ queryKey: ['workflow-template', variables.templateId] });
      queryClient.invalidateQueries({ queryKey: ['template-validation', variables.templateId] });
    },
  });
};

export const useCopyTemplateVariables = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ destTemplateId, sourceTemplateId }: { destTemplateId: string; sourceTemplateId: string }) =>
      api!.copyTemplateVariables(destTemplateId, sourceTemplateId),
    onSuccess: (_, { destTemplateId }) => {
      queryClient.invalidateQueries({ queryKey: ['template-variables', destTemplateId] });
      queryClient.invalidateQueries({ queryKey: ['workflow-template', destTemplateId] });
      queryClient.invalidateQueries({ queryKey: ['template-validation', destTemplateId] });
    },
  });
};

// Workflow Type Defaults
export const useWorkflowTypeDefaults = (
  params?: { type_name?: string; environment?: string },
  enabled = true
) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['workflow-type-defaults', params],
    queryFn: () => api!.getWorkflowTypeDefaults(params),
    staleTime: 30_000,
    enabled: !!api && enabled,
  });
};

export const useCreateWorkflowTypeDefault = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateWorkflowTypeDefaultPayload) =>
      api!.createWorkflowTypeDefault(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-type-defaults'] });
    },
  });
};

export const useUpdateWorkflowTypeDefault = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Partial<CreateWorkflowTypeDefaultPayload>;
    }) => api!.updateWorkflowTypeDefault(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-type-defaults'] });
    },
  });
};

export const useDeleteWorkflowTypeDefault = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api!.deleteWorkflowTypeDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-type-defaults'] });
    },
  });
};

// Resolution Testing
export const useTestResolution = () => {
  const api = useApiClient();

  return useMutation({
    mutationFn: (request: ResolutionTestRequest) => api!.testResolution(request),
  });
};

// Create Job from Template
export const useCreateJobFromTemplate = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateJobFromTemplatePayload) => api!.createJobFromTemplate(payload),
    onSuccess: () => {
      // Invalidate jobs queries to refetch with new job
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['jobs-infinite'] });
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
    },
  });
};
